/**
 * "Support Directly" — a monastery's own donation link, distinct from the
 * offerings GOMPA facilitates.
 *
 * The card exists to make four things unambiguous before a donor acts:
 *   1. this goes directly to the monastery, not through GOMPA;
 *   2. GOMPA has reviewed the link;
 *   3. 100% of the donation reaches the monastery — GOMPA is not in the path;
 *   4. the donor is about to leave GOMPA for an external page.
 *
 * The verified badge is data-driven: it appears only when `verified` is true.
 * There is no generic or placeholder verification state — an unreviewed link
 * shows the card with no badge at all. What GOMPA claims is narrow on purpose
 * ("confirmed it is associated with this monastery"), never a guarantee of the
 * destination, the payment, or how the funds are used.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import DonationConfirmModal from './DonationConfirmModal';
import { formatDate } from '../lib/format';
import { trackEvent } from '../lib/consent';

export default function DirectSupport({ monasteryName, monasteryId, donationUrl, verified = false, verifiedAt = null }) {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const badgeRef = useRef(null);

  // Reveal is hover/focus first (CSS), but a tap pins it open — so a tap
  // elsewhere, or Escape, closes it again.
  useEffect(() => {
    if (!detailOpen) return undefined;
    const onPointerDown = (event) => {
      if (!badgeRef.current?.contains(event.target)) setDetailOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setDetailOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [detailOpen]);

  if (!donationUrl) return null;

  const toggleDetail = () => {
    setDetailOpen((open) => {
      if (!open) trackEvent('donation_verification_viewed', { item_id: monasteryId });
      return !open;
    });
  };

  return (
    <section className="direct-support" aria-labelledby="direct-support-title">
      <div className="direct-support__header">
        <h3 id="direct-support-title" className="direct-support__title">
          {t('donation.sectionTitle', 'Support Directly')}
        </h3>

        {verified && (
          <div className="verified-badge" ref={badgeRef}>
            <button
              type="button"
              className="verified-pill"
              aria-expanded={detailOpen}
              aria-describedby="verified-pop-text"
              onClick={toggleDetail}
            >
              {t('donation.verifiedLabel', 'Verified by GOMPA')}
              <Icon name="chevronDown" size={12} className={`verified-pill__caret${detailOpen ? ' is-open' : ''}`} />
            </button>

            <div className={`verified-pop${detailOpen ? ' is-open' : ''}`} role="note">
              <div className="verified-pop__inner" id="verified-pop-text">
                <p className="verified-pop__body">
                  {t(
                    'donation.verifiedDetailBody',
                    'GOMPA reviewed this donation link and confirmed that it is associated with this monastery.'
                  )}
                </p>
                {verifiedAt && (
                  <p className="verified-pop__date">
                    {t('donation.verifiedDetailDate', {
                      date: formatDate(verifiedAt),
                      defaultValue: `Last verified: ${formatDate(verifiedAt)}`,
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="direct-support__body">
        {t('donation.directBody', {
          name: monasteryName,
          defaultValue: `Donate directly to ${monasteryName} through their verified donation link.`,
        })}
      </p>

      <p className="direct-support__fee">
        {t('donation.feeStatement', {
          name: monasteryName,
          defaultValue: `100% of your donation goes to ${monasteryName}.`,
        })}
      </p>

      <button
        type="button"
        className="btn direct-support__cta"
        onClick={() => setModalOpen(true)}
        aria-describedby="direct-support-cta-hint"
      >
        {t('donation.ctaButton', 'Donate Directly')}
        <Icon name="external" size={16} />
      </button>
      <span id="direct-support-cta-hint" className="visually-hidden">
        {t('donation.ctaHint', 'Opens a confirmation, then the monastery’s own donation page on an external site.')}
      </span>

      {modalOpen && (
        <DonationConfirmModal
          monasteryName={monasteryName}
          monasteryId={monasteryId}
          donationUrl={donationUrl}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
}
